import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Plus, Pencil, X, Trash2, ChevronDown, ChevronRight, ArrowUpRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { useAuth } from '../context/AuthContext'
import CsvControls from '../components/CsvControls'

interface Category {
  id: string; name: string; name_ar: string | null; slug: string
  sort_order: number; product_count: number
}

interface ProductRow { id: string; name: string; stock: number; active: number }

const schema = z.object({
  name: z.string().min(1, 'Required'),
  name_ar: z.string().optional(),
  slug: z.string().min(1, 'Required').regex(/^[a-z0-9-]+$/, 'Lowercase letters, numbers, hyphens only'),
  sort_order: z.number().int().min(0),
})
type FormData = z.infer<typeof schema>

export default function Categories() {
  const { can } = useAuth()
  const navigate = useNavigate()
  const [modal, setModal] = useState<Category | null | 'new'>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<Category | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const qc = useQueryClient()

  const { data } = useQuery<{ categories: Category[] }>({
    queryKey: ['categories'],
    queryFn: () => api.get('/categories').then(r => r.data),
  })

  const canEdit = can(['super_admin', 'operations'])
  const canDelete = can(['super_admin'])

  function toggleExpand(id: string) {
    setExpandedId(prev => prev === id ? null : id)
  }

  function handleDeleteClick(cat: Category) {
    if (cat.product_count > 0) {
      setDeleteError(
        `Cannot delete "${cat.name}" — ${cat.product_count} active product${cat.product_count === 1 ? '' : 's'} assigned. Reassign or deactivate them first.`
      )
      return
    }
    setDeleteConfirm(cat)
  }

  async function confirmDelete(cat: Category) {
    await api.delete(`/categories/${cat.id}`)
    qc.invalidateQueries({ queryKey: ['categories'] })
    setDeleteConfirm(null)
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-lg font-semibold text-white">Categories</h1>
        {canEdit && (
          <div className="flex items-center gap-2">
            <CsvControls
              basePath="/categories"
              templateFilename="categories-template.csv"
              exportFilename={`categories-export-${new Date().toISOString().slice(0, 10)}.csv`}
              label="category"
              onImported={() => qc.invalidateQueries({ queryKey: ['categories'] })}
            />
            <button
              onClick={() => setModal('new')}
              className="flex items-center gap-2 bg-[#C41E3A] hover:bg-[#a51830] text-white text-sm px-4 py-2 rounded-lg transition-colors"
            >
              <Plus size={15} /> Add Category
            </button>
          </div>
        )}
      </div>

      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-600 border-b border-[#2e2e2e]">
              <th className="px-4 py-3 text-left font-medium w-6"></th>
              {['Name', 'Arabic Name', 'Slug', 'Sort', 'Products', ''].map(h => (
                <th key={h} className="px-4 py-3 text-left font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.categories.map((c) => (
              <>
                <tr key={c.id} className="border-b border-[#222] hover:bg-white/2 transition-colors">
                  <td className="px-3 py-3">
                    <button
                      onClick={() => toggleExpand(c.id)}
                      className="text-gray-600 hover:text-gray-300 transition-colors"
                      title={expandedId === c.id ? 'Collapse' : 'Expand'}
                    >
                      {expandedId === c.id
                        ? <ChevronDown size={14} />
                        : <ChevronRight size={14} />}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-white font-medium">{c.name}</td>
                  <td className="px-4 py-3 text-gray-400">{c.name_ar ?? '—'}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">{c.slug}</td>
                  <td className="px-4 py-3 text-gray-400">{c.sort_order}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => navigate(`/products?category=${c.id}`)}
                      className="text-gray-400 hover:text-white transition-colors tabular-nums"
                      title="View products"
                    >
                      {c.product_count}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3 justify-end">
                      {canEdit && (
                        <button onClick={() => setModal(c)} className="text-gray-500 hover:text-white transition-colors" title="Edit">
                          <Pencil size={14} />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => handleDeleteClick(c)}
                          className="text-gray-600 hover:text-red-400 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>

                {expandedId === c.id && (
                  <tr key={`${c.id}-expand`} className="border-b border-[#222] bg-[#141414]">
                    <td colSpan={7} className="px-8 py-3">
                      <CategoryProducts
                        categoryId={c.id}
                        categoryName={c.name}
                        onViewAll={() => navigate(`/products?category=${c.id}`)}
                      />
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delete error alert */}
      {deleteError && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
          <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl w-full max-w-sm p-5">
            <h2 className="text-sm font-semibold text-white mb-2">Cannot Delete</h2>
            <p className="text-sm text-gray-400 mb-4">{deleteError}</p>
            <div className="flex justify-end">
              <button
                onClick={() => setDeleteError(null)}
                className="px-4 py-2 bg-[#C41E3A] hover:bg-[#a51830] text-white text-sm rounded-lg"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
          <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl w-full max-w-sm p-5">
            <h2 className="text-sm font-semibold text-white mb-2">Delete category?</h2>
            <p className="text-sm text-gray-400 mb-4">
              Delete <span className="text-white font-medium">"{deleteConfirm.name}"</span>? This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-sm text-gray-400 hover:text-white">
                Cancel
              </button>
              <button
                onClick={() => confirmDelete(deleteConfirm)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {modal !== null && (
        <CategoryModal
          category={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['categories'] }); setModal(null) }}
        />
      )}
    </div>
  )
}

function CategoryProducts({ categoryId, categoryName, onViewAll }: {
  categoryId: string; categoryName: string; onViewAll: () => void
}) {
  const { data, isLoading } = useQuery<{ products: ProductRow[] }>({
    queryKey: ['category-products', categoryId],
    queryFn: () => api.get(`/products?category=${categoryId}&limit=500`).then(r => r.data),
  })

  if (isLoading) return <p className="text-xs text-gray-600 py-1">Loading…</p>

  const products = data?.products ?? []

  if (products.length === 0) {
    return <p className="text-xs text-gray-600 py-1">No products in this category.</p>
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-gray-500">{products.length} product{products.length === 1 ? '' : 's'} in {categoryName}</p>
        <button
          onClick={onViewAll}
          className="flex items-center gap-1 text-xs text-[#C41E3A] hover:text-[#e02244] transition-colors"
        >
          View all in Products <ArrowUpRight size={11} />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-x-8 gap-y-1">
        {products.map(p => (
          <div key={p.id} className="flex items-center gap-2 py-0.5">
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${p.stock > 0 ? 'bg-green-500' : 'bg-gray-600'}`} />
            <span className="text-xs text-gray-300 truncate">{p.name}</span>
            {p.stock === 0 && <span className="text-xs text-gray-600 flex-shrink-0">OOS</span>}
          </div>
        ))}
      </div>
    </div>
  )
}

function CategoryModal({ category, onClose, onSaved }: {
  category: Category | null; onClose: () => void; onSaved: () => void
}) {
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: category ? {
      name: category.name, name_ar: category.name_ar ?? '',
      slug: category.slug, sort_order: category.sort_order,
    } : { sort_order: 0 },
  })

  async function onSubmit(data: FormData) {
    if (category) {
      await api.put(`/categories/${category.id}`, data)
    } else {
      await api.post('/categories', data)
    }
    onSaved()
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2e2e2e]">
          <h2 className="text-sm font-semibold text-white">{category ? 'Edit Category' : 'Add Category'}</h2>
          <button onClick={onClose}><X size={16} className="text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="p-5 space-y-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Name (EN)</label>
            <input {...register('name')} className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]" />
            {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name.message}</p>}
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Name (AR)</label>
            <input {...register('name_ar')} dir="rtl" className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Slug</label>
              <input {...register('slug')} className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-[#C41E3A]" placeholder="whisky" />
              {errors.slug && <p className="text-xs text-red-400 mt-1">{errors.slug.message}</p>}
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Sort Order</label>
              <input {...register('sort_order', { valueAsNumber: true })} type="number" className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]" />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
            <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-[#C41E3A] hover:bg-[#a51830] disabled:opacity-50 text-white text-sm rounded-lg">
              {isSubmitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
